import { memo, useCallback } from 'react';
import { useSelector } from '@xstate/store/react';
import { ALL_CONNECTOR_SIDES, MIN_SHAPE_WIDTH, MIN_SHAPE_HEIGHT } from '../../entityTypes';
import type { CanvasEntity } from '../../entityTypes';
import { getShapeData } from '../../entityTypes';
import { useCanvasStores } from '../../stores/CanvasContext';
import { useMergedDropTarget } from '../../hooks/useMergedDropTarget';
import { useLockedLayerIds } from '../../hooks/useLockedLayerIds';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { useEntityData } from '../../stores/EntityDataContext';
import { CanvasConnector } from '../../components/CanvasConnector';
import { CanvasResizeHandles } from '../../components/CanvasResizeHandles';

export interface CanvasShapeProps {
  entity: CanvasEntity;
}

function CanvasShapeInner({ entity }: CanvasShapeProps) {
  const { store } = useCanvasStores();
  const { mergedDropTargetId, mergedDropTargetSide } = useMergedDropTarget();
  const { selectedEdgeConnectors, connectorHasEdge } = useEntityData();
  const lockedLayerIds = useLockedLayerIds();
  const d = getShapeData(entity);

  const isSelected = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds.includes(entity.id));
  const isLocked = lockedLayerIds.has(entity.layerId);
  const isDropTarget = mergedDropTargetId === entity.id;
  const dropTargetSide = isDropTarget ? mergedDropTargetSide : null;

  const handleResize = useCallback((x: number, y: number, width: number, height: number) => {
    store.send({ type: 'entity.resize', entityId: entity.id, x, y, width, height });
  }, [store, entity.id]);

  const w = entity.width;
  const h = entity.height;

  // SVG stroke offset so border is not clipped
  const sw = d.borderColor ? (d.borderWidth ?? 1) : 0;
  const half = sw / 2;

  const bg = d.backgroundColor;
  const borderColor = d.borderColor;

  return (
    <div
      data-canvas-entity
      data-canvas-shape={entity.id}
      data-entity-id={entity.id}
      data-entity-kind={entity.kind}
      style={{
        position: 'absolute',
        left: entity.x,
        top: entity.y,
        width: w,
        height: h,
      }}
      className={`select-none ${isSelected ? 'ring-2 ring-blue-400' : ''} ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        {d.shapeType === 'rect' && (
          <rect
            x={half}
            y={half}
            width={w - sw}
            height={h - sw}
            rx={d.cornerRadius ?? 0}
            fill={bg}
            stroke={borderColor}
            strokeWidth={sw}
          />
        )}
        {d.shapeType === 'ellipse' && (
          <ellipse
            cx={w / 2}
            cy={h / 2}
            rx={w / 2 - half}
            ry={h / 2 - half}
            fill={bg}
            stroke={borderColor}
            strokeWidth={sw}
          />
        )}
        {d.shapeType === 'diamond' && (
          <polygon
            points={`${w / 2},${half} ${w - half},${h / 2} ${w / 2},${h - half} ${half},${h / 2}`}
            fill={bg}
            stroke={borderColor}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        )}
        {d.shapeType === 'triangle' && (
          <polygon
            points={`${w / 2},${half} ${w - half},${h - half} ${half},${h - half}`}
            fill={bg}
            stroke={borderColor}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        )}
      </svg>

      {/* Resize handles when selected */}
      {isSelected && !isLocked && (
        <CanvasResizeHandles
          x={entity.x}
          y={entity.y}
          width={w}
          height={h}
          minWidth={MIN_SHAPE_WIDTH}
          minHeight={MIN_SHAPE_HEIGHT}
          onResize={handleResize}
        />
      )}

      {/* Connectors */}
      {entity.connectable && ALL_CONNECTOR_SIDES.map(side => {
        const key = `${entity.id}:${side}`;
        return (
          <CanvasConnector
            key={side}
            taskId={entity.id}
            entityWidth={w}
            entityHeight={h}
            side={side}
            isDropTarget={isDropTarget}
            dropTargetSide={dropTargetSide}
            isEdgeEndpoint={selectedEdgeConnectors.has(key)}
            hasEdge={connectorHasEdge.has(key)}
          />
        );
      })}
    </div>
  );
}

export const CanvasShape = memo(CanvasShapeInner);
